import { defineComponent, reactive, watch, nextTick, onMounted, computed, ref } from 'vue';
import { useStore } from 'vuex';
import moment from 'moment';
import { withLoading } from '@hems/component';
import LogicalCanvas from '@hems/component/src/mapper/canvas/LogicalCanvas.vue';
import PhysicalCanvasHome from '@hems/component/src/mapper/canvas/PhysicalCanvasHome.vue';
import { SmartModuleService, UserReportService } from '@hems/service';
import { DateHelper, useJsonFileConverter } from '@hems/util';
import { GRAPH_INTERVAL, NORMAL_DATE_HOUR_MIN_FORMAT } from '@hems/util/src/constant';
import { MAPPER_APP_TYPE, MAPPER_PERIOD_DATA, MAPPER_POWER_DEFAULT_VALUE } from '@hems/util/src/constant/mapper';
export default defineComponent({
    name: 'SmartModuleMobileHomeContainer',
    components: {
        PhysicalCanvasHome,
        LogicalCanvas,
    },
    props: {
        siteId: {
            type: Number,
            required: true,
        },
        width: {
            type: Number,
            default: 0,
        },
        height: {
            type: Number,
            default: 0,
        },
        selectedDate: {
            type: String,
            default: '',
        },
    },
    emits: ['changeDateMode', 'openMobileCalendar', 'openWebCalendar', 'changeViewTab'],
    setup(props, { emit }) {
        const PADDING = 49;
        const smartModuleService = new SmartModuleService(window.axiosInstance.axios);
        const store = useStore();
        const today = DateHelper.formatToday('YYYYMMDD');
        const siteId = computed(() => props.siteId);
        const selectedDateValue = computed(() => (props.selectedDate?.length === 0 ? today : props.selectedDate));
        const state = reactive({
            logicalData: { combinerId: '', deviceIdList: [] },
            tabName: 'roof',
            height: props.height ? props.height - PADDING : window.innerHeight - PADDING,
            moduleEnergyData: [],
            module15minPwData: [],
            dateType: MAPPER_PERIOD_DATA.DAY,
            physicalData: '',
        });
        const { device_id: combinerId, timezone_id: timezoneId } = store.state.device.baseDeviceInfo;
        const isLoad = ref(false);
        function rerender() {
            isLoad.value = false;
            nextTick(() => (isLoad.value = true));
        }
        const isRoofShow = computed(() => state.tabName === 'roof' && isLoad.value);
        const setLogicalData = (moduleInfo) => {
            state.logicalData = {
                combinerId,
                deviceIdList: moduleInfo.map((info) => ({ deviceId: info.device_id, moduleId: info.module_id })),
            };
        };
        const { convertJsonFileForMicroInverterUpdateActivity, findMappedSerial } = useJsonFileConverter();
        const getMicroInverterActivities = async (canvasJsonData) => {
            try {
                return await smartModuleService.getMicroInverterActivities(siteId.value, combinerId, {
                    sort: 'create_dt',
                });
            }
            catch (error) {
                console.error(error);
                return [];
            }
            finally {
                state.physicalData = canvasJsonData;
            }
        };
        const onLoad = () => {
            if (!siteId.value)
                return;
            withLoading(async () => {
                try {
                    state.physicalData = await smartModuleService.getDrawingFileBySiteId(props.siteId);
                    const moduleInfo = await smartModuleService.getModulesInfoBySiteId(siteId.value);
                    if (moduleInfo && moduleInfo.length > 0) {
                        setLogicalData(moduleInfo);
                    }
                    const canvasJsonData = await smartModuleService.getDrawingFileBySiteId(siteId.value);
                    const microInverterActivities = await getMicroInverterActivities(canvasJsonData);
                    const recentMicroInverterList = moduleInfo.map((module) => module.device_id);
                    /** API로부터 가져온 Json File에 mapping된 리스트 */
                    const mappedSerialListInJsonFile = findMappedSerial(canvasJsonData);
                    const targetMicroInverterIdList = mappedSerialListInJsonFile?.filter((serial) => !recentMicroInverterList.includes(serial));
                    const isPossibleJsonFileUpdateFromActivities = targetMicroInverterIdList.length > 0 && microInverterActivities.length > 0;
                    if (isPossibleJsonFileUpdateFromActivities) {
                        state.physicalData = convertJsonFileForMicroInverterUpdateActivity(canvasJsonData, recentMicroInverterList, targetMicroInverterIdList, microInverterActivities);
                        return;
                    }
                    state.physicalData = canvasJsonData;
                }
                catch (error) {
                    console.log(error);
                }
            })();
        };
        const totalPowerValue = ref(MAPPER_POWER_DEFAULT_VALUE);
        const service = new UserReportService(window.axiosInstance.axios);
        const loadTotalEnergyValue = () => {
            if (!siteId.value)
                return;
            const { type, condition } = getEnergyDataCondition();
            const { from, to } = condition;
            const start_date = state.dateType === MAPPER_PERIOD_DATA.LIFETIME
                ? moment(store.state.device.installation?.install_dt).format('YYYY')
                : moment(from).format('YYYYMMDD');
            const end_date = state.dateType === MAPPER_PERIOD_DATA.LIFETIME ? moment(to).format('YYYY') : moment(to).format('YYYYMMDD');
            withLoading(async () => {
                try {
                    const moduleDayEnergyData = await service.getReportsByInterval(type, {
                        from: start_date,
                        to: end_date,
                        type: 'gen',
                        ...store.state.device.baseDeviceInfo,
                    });
                    totalPowerValue.value = moduleDayEnergyData.result.gen_kwh;
                }
                catch (error) {
                    console.log(error);
                }
            })();
        };
        const loadEnergyData = () => {
            if (!props.siteId)
                return;
            const { type, condition } = getEnergyDataCondition();
            withLoading(async () => {
                try {
                    const moduleDayEnergyData = await smartModuleService.getEnergyMonitoringInfoBySiteId(props.siteId, type, condition);
                    state.moduleEnergyData = moduleDayEnergyData.map((data) => ({
                        device_id: data.device_id,
                        total_power: data.total_power,
                    }));
                }
                catch (error) {
                    console.log(error);
                }
            })();
        };
        function getEnergyDataCondition() {
            const format = 'YYYY-MM-DD';
            let param = 'daily';
            let momentParam = 'day';
            if (state.dateType === MAPPER_PERIOD_DATA.DAY) {
                param = GRAPH_INTERVAL.DAILY;
                momentParam = 'day';
            }
            else if (state.dateType === MAPPER_PERIOD_DATA.MONTH) {
                param = GRAPH_INTERVAL.MONTHLY;
                momentParam = 'month';
            }
            else if (state.dateType === MAPPER_PERIOD_DATA.YEAR) {
                param = GRAPH_INTERVAL.YEARLY;
                momentParam = 'year';
            }
            else if (state.dateType === MAPPER_PERIOD_DATA.LIFETIME) {
                param = GRAPH_INTERVAL.YEARLY;
                momentParam = 'year';
            }
            const start_date = moment(selectedDateValue.value).startOf(momentParam).format(`${format} 00:00:00`);
            const end_date = moment(selectedDateValue.value).endOf(momentParam).format(`${format} 23:59:59`);
            const condition = {
                from: start_date,
                to: end_date,
                timezoneId,
            };
            return { type: param, condition };
        }
        const load15MinPowerData = () => {
            if (!props.siteId)
                return;
            if (state.dateType === MAPPER_PERIOD_DATA.DAY) {
                const params = getEnergyDataCondition();
                withLoading(async () => {
                    try {
                        const module15MinPowerData = await smartModuleService.get15MinPvPowerInfoACBySiteId(props.siteId, params.condition);
                        state.module15minPwData = module15MinPowerData.filter((moduleData) => (moduleData.colec_dt = Number(moment(moduleData.colec_dt).format(NORMAL_DATE_HOUR_MIN_FORMAT))));
                    }
                    catch (error) {
                        console.log(error);
                    }
                })();
            }
        };
        onMounted(() => {
            onLoad();
            loadTotalEnergyValue();
            loadEnergyData();
            load15MinPowerData();
        });
        function changeDateMode(value) {
            state.dateType = value;
        }
        function onClickTab(tabName) {
            state.tabName = tabName;
            emit('changeViewTab');
            state.dateType = MAPPER_PERIOD_DATA.DAY;
        }
        const openCalendar = () => {
            if (state.dateType !== MAPPER_PERIOD_DATA.LIFETIME) {
                if (location.href.includes('/mobile/')) {
                    emit('openMobileCalendar', state.dateType);
                }
                else {
                    emit('openWebCalendar', state.dateType);
                    return;
                }
            }
        };
        watch(() => state.physicalData, () => {
            rerender();
        });
        watch(() => [state.dateType, selectedDateValue.value], () => {
            loadTotalEnergyValue();
            loadEnergyData();
            load15MinPowerData();
        });
        watch(() => props.siteId, () => {
            onLoad();
            loadTotalEnergyValue();
            loadEnergyData();
            load15MinPowerData();
        });
        return {
            state,
            onClickTab,
            changeDateMode,
            openCalendar,
            totalPowerValue,
            MAPPER_APP_TYPE,
            isLoad,
            selectedDateValue,
            isRoofShow,
        };
    },
});
